import React from "react";
import api from "@/lib/api";

/**
 * Activity heatmap: 7 days × 24 hours grid colored by count.
 */
export default function ActivityHeatmap() {
  const [data, setData] = React.useState(null);
  const [days, setDays] = React.useState(30);

  React.useEffect(() => {
    api.get(`/admin/heatmap?days=${days}`).then((r) => setData(r.data)).catch(() => {});
  }, [days]);

  if (!data) return null;

  const max = Math.max(1, ...data.grid.flat());
  // Color scale: 0 -> dark; max -> bright orange
  const colorFor = (v) => {
    if (v === 0) return "rgba(255,255,255,0.04)";
    const t = v / max;
    // Lerp dark zinc -> orange-500
    const r = Math.round(24 + (249 - 24) * t);
    const g = Math.round(24 + (115 - 24) * t);
    const b = Math.round(27 + (22 - 27) * t);
    return `rgb(${r},${g},${b})`;
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-6" data-testid="heatmap-card">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h3 className="font-outfit font-bold text-white text-lg">
            Heatmap activitate · ore × zile
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            {data.total_events} evenimente în ultimele {data.days_covered} zile (login, simulări,
            recomandări).
          </p>
        </div>
        <div className="flex items-center gap-1 border border-white/10 rounded-md p-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded text-xs uppercase tracking-[0.15em] transition ${
                days === d
                  ? "bg-orange-500 text-white"
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid={`heatmap-range-${d}`}
            >
              {d} zile
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Hours header */}
          <div className="flex items-center gap-1 mb-1 pl-10">
            {hours.map((h) => (
              <div
                key={h}
                className="w-6 text-center text-[9px] text-zinc-500 font-mono"
              >
                {h % 3 === 0 ? h.toString().padStart(2, "0") : ""}
              </div>
            ))}
          </div>
          {/* Days rows */}
          {data.day_labels.map((day, dow) => (
            <div key={day} className="flex items-center gap-1 mb-1">
              <div className="w-8 text-xs text-zinc-400 font-medium">{day}</div>
              <div className="w-1" />
              {hours.map((h) => {
                const v = data.grid[dow][h];
                return (
                  <div
                    key={h}
                    title={`${day} ${h.toString().padStart(2, "0")}:00 · ${v} evenimente`}
                    className="w-6 h-6 rounded-sm transition-transform hover:scale-125 hover:ring-1 hover:ring-orange-400 cursor-pointer"
                    style={{ backgroundColor: colorFor(v) }}
                    data-testid={`hm-${dow}-${h}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-5 text-xs text-zinc-500">
        <span>Mai puțin</span>
        <div className="flex gap-1">
          {[0, 0.2, 0.4, 0.6, 0.8, 1].map((t) => {
            const r = Math.round(24 + (249 - 24) * t);
            const g = Math.round(24 + (115 - 24) * t);
            const b = Math.round(27 + (22 - 27) * t);
            return (
              <div
                key={t}
                className="w-4 h-4 rounded-sm"
                style={{ backgroundColor: t === 0 ? "rgba(255,255,255,0.04)" : `rgb(${r},${g},${b})` }}
              />
            );
          })}
        </div>
        <span>Mai mult</span>
        <span className="ml-auto text-orange-400">Max: {max}</span>
      </div>
    </div>
  );
}
